import React from 'react';

import { type FlyThemeColor, Heading, Text, type TextSize } from '@fly/core';
import cn from 'classnames';

import { FlyMarkdownRenderer } from '../FlyMarkdownRenderer/FlyMarkdownRenderer';

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4';
type HeadingSize = '1' | '2' | '3' | '4';

const HEADING_CLASS = 'font-semibold p-0! mt-fly-4! mb-fly-2! border-0!';

function SummaryHeading({
  as,
  size,
  textSizeClass,
  color,
  children,
}: {
  as: HeadingTag;
  size: HeadingSize;
  textSizeClass: string;
  color?: FlyThemeColor | null;
  children?: React.ReactNode;
}) {
  return (
    <Heading
      as={as}
      size={size}
      highContrast
      className={cn(HEADING_CLASS, textSizeClass)}
      {...(color != null ? { color } : {})}
    >
      {children}
    </Heading>
  );
}

export type FlySummaryMarkdownProps = {
  summary: string;
  className?: string;
  /** When omitted or null, `color` is not passed to typography (Radix/theme defaults). */
  color?: FlyThemeColor | null;
  /** `size` on paragraph `<Text>`; default `'2'`. */
  paragraphTextSize?: TextSize;
  /** `size` on list-item `<Text>`; default `'3'`. */
  listItemTextSize?: TextSize;
  ref?: React.Ref<HTMLElement>;
};

export function FlySummaryMarkdown({
  summary,
  className,
  color,
  paragraphTextSize = '2',
  listItemTextSize = '3',
  ref,
}: FlySummaryMarkdownProps) {
  return (
    <FlyMarkdownRenderer
      ref={ref as React.Ref<HTMLDivElement>}
      className={className}
      markdown={summary}
      components={{
        p: ({ node, children }) => (
          <Text
            size={paragraphTextSize}
            as='p'
            className='font-semibold mb-fly-4!'
            {...(color != null ? { color } : {})}
          >
            {children}
          </Text>
        ),
        h1: ({ node, children }) => (
          <SummaryHeading
            as='h1'
            size='4'
            textSizeClass='text-size-fly-4!'
            color={color}
          >
            {children}
          </SummaryHeading>
        ),
        h2: ({ node, children }) => (
          <SummaryHeading
            as='h2'
            size='3'
            textSizeClass='text-size-fly-3!'
            color={color}
          >
            {children}
          </SummaryHeading>
        ),
        h3: ({ node, children }) => (
          <SummaryHeading
            as='h3'
            size='2'
            textSizeClass='text-size-fly-2!'
            color={color}
          >
            {children}
          </SummaryHeading>
        ),
        h4: ({ node, children }) => (
          <SummaryHeading
            as='h4'
            size='1'
            textSizeClass='text-size-fly-1!'
            color={color}
          >
            {children}
          </SummaryHeading>
        ),
        li: ({ node, children }) => (
          <li>
            <Text
              as='div'
              size={listItemTextSize}
              className='font-semibold'
              {...(color != null ? { color } : {})}
            >
              {children}
            </Text>
          </li>
        ),
      }}
    />
  );
}
